// components/common/DownloadApp.jsx
import React from "react";
import appPreview from "../../assets/tap2cash-app-preview.webp";

const DownloadApp = ({
  isDarkMode = false,
  variant = "default", // "default" | "compact"
  showPhone = true,
  className = "",
}) => {
  const isCompact = variant === "compact";

  return (
    <div
      className={`relative overflow-hidden rounded-2xl ${
        isCompact ? "px-4 py-4 sm:px-6 sm:py-5" : "px-5 py-6 sm:px-8 sm:py-7"
      } ${
        isDarkMode
          ? "bg-gradient-to-br from-gray-800 via-gray-900 to-black"
          : "bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50"
      } ${className}`}
    >
      {/* Decorative glow blobs */}
      <div className="absolute w-40 h-40 rounded-full pointer-events-none -top-12 -right-12 bg-green-400/20 blur-3xl" />
      <div className="absolute w-40 h-40 rounded-full pointer-events-none -bottom-12 -left-12 bg-emerald-400/20 blur-3xl" />

      <div
        className={`relative grid grid-cols-1 ${
          showPhone ? "lg:grid-cols-2" : ""
        } gap-5 items-center`}
      >
        {/* Left: text + features + Play button */}
        <div className="text-center lg:text-left">
          <div className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-semibold mb-2 bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
            <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
            Now available on Android
          </div>

          <h3
            className={`${
              isCompact ? "text-lg sm:text-xl" : "text-xl sm:text-2xl"
            } font-extrabold tracking-tight ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Download our app now
          </h3>

          <p
            className={`mt-1.5 text-[12px] font-medium max-w-md mx-auto lg:mx-0 ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Sell your old device in under 60 seconds. Instant quote, free
            pickup, same-day payment.
          </p>

          {/* Feature pills */}
          <div className="mt-3 flex flex-wrap justify-center lg:justify-start gap-1.5">
            {["Instant Quote", "Free Pickup", "Best Price", "Secure"].map(
              (feature) => (
                <span
                  key={feature}
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${
                    isDarkMode
                      ? "bg-gray-800 text-gray-200 ring-1 ring-gray-700"
                      : "bg-white text-gray-700 ring-1 ring-green-200 shadow-sm"
                  }`}
                >
                  <svg
                    className="w-2.5 h-2.5 text-green-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.707a1 1 0 00-1.414-1.414L9 10.172 7.707 8.879a1 1 0 10-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {feature}
                </span>
              )
            )}
          </div>

          {/* Play Store button */}
          <div className="flex justify-center mt-4 lg:justify-start">
            <a
              href="https://play.google.com/store/apps/details?id=com.tap2cash.mobile"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download Tap2Cash from Google Play"
              className="group relative inline-flex items-center gap-2.5 rounded-xl bg-black px-4 py-2.5 text-white shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-2xl active:translate-y-0"
            >
              {/* Shine */}
              <span className="absolute inset-0 overflow-hidden pointer-events-none rounded-xl">
                <span className="absolute -left-1/2 top-0 h-full w-1/3 -skew-x-12 bg-white/20 blur-md transition-transform duration-700 group-hover:translate-x-[300%]" />
              </span>

              {/* Play triangle */}
              <svg viewBox="0 0 512 512" className="flex-shrink-0 h-7 w-7">
                <path fill="#00d3ff" d="M47 24l265 232L47 488z" />
                <path fill="#00f076" d="M47 24l322 168-57 64z" />
                <path fill="#ffce00" d="M312 256l57 64L47 488z" />
                <path
                  fill="#ff3a44"
                  d="M369 192l96 50c14 7 14 21 0 28l-96 50-57-64z"
                />
              </svg>

              <span className="flex flex-col items-start leading-none">
                <span className="text-[9px] uppercase tracking-wider text-gray-300">
                  Get it on
                </span>
                <span className="text-sm font-semibold">Google Play</span>
              </span>
            </a>
          </div>
        </div>

        {/* Right: real app screenshot in phone frame */}
        {showPhone && (
          <div className="relative items-center justify-center hidden lg:flex">
            <div className="relative">
              {/* Glow behind phone */}
              <div className="absolute inset-0 rounded-[2rem] bg-green-400/30 blur-3xl" />

              {/* Phone frame */}
              <div
                className={`relative w-[170px] h-[340px] rounded-[2rem] border-[5px] shadow-2xl overflow-hidden animate-[float_4s_ease-in-out_infinite] ${
                  isDarkMode
                    ? "border-gray-700 bg-gray-900"
                    : "border-gray-900 bg-white"
                }`}
              >
                {/* Notch */}
                <div className="absolute top-0 z-20 w-20 h-4 -translate-x-1/2 bg-gray-900 left-1/2 rounded-b-xl" />

                {/* Real screenshot */}
                <img
                  src={appPreview}
                  alt="Tap2Cash app preview"
                  className="absolute inset-0 object-cover object-top w-full h-full"
                  loading="lazy"
                />

                {/* Subtle inner shadow */}
                <div className="pointer-events-none absolute inset-0 rounded-[1.75rem] ring-1 ring-black/5" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DownloadApp;